import React from 'react';
import {PropTypes} from "prop-types";
import styled, {useTheme} from "styled-components";
import {useSelector} from "react-redux";
import { getTranslation } from '../translations/translationUtils';
import DetailImage from "./DetailImage";
import {formatDefaultDateString} from "../utils/dateTimeUtils";
import {capitalizeFirstLetter} from "../utils/stringUtils";

const WeatherInfoContainer = styled.div`
    padding: 1em;
    flex-grow: 1;
    align-self: center;
`;

const WeatherBoxContainer = styled.div`
    background: ${props => props.theme.subheaderBackground || "rgb(112, 203, 244);"};
    color: ${props => props.theme.subheaderColor || "#000000"};
`;

const WeatherInformation = ({weather}, context) => {
    const theme = useTheme();
    const hubUrl = useSelector(state => state.configuration.hubUrl);
    const isWeatherDataAvailable = weather.schedule.openWeatherCityName !== null && weather.synopsis != null;
    const temperatureUnit = weather.unit === "imperial" ? "°F" : "°C"

    const weatherObject = {
        synopsis: { label: context.t("synopsis"), value: capitalizeFirstLetter(weather.synopsis) },
        temperature: { label: context.t("temperature"), value: `${Math.trunc(weather.temperature)}`, unit: temperatureUnit },
        feelsLike: {label: context.t("feels_like"), value: `${Math.trunc(weather.feelsLike)}`, unit: temperatureUnit },
        wind: { label: context.t("wind"), value: weather.windDirection && weather.windSpeed && `${weather.windDirection} ${Math.trunc(weather.windSpeed)}`, unit:  weather.unit === "imperial" ? "mph" : "km/h" },
        humidity: { label: context.t("humidity"), value: `${weather.humidity}`, unit: "%" }
    }

    const allAboard = {
        date: { label: context.t("date"), value: formatDefaultDateString(weather.schedule?.date, theme.dateLocale) },
        arrives: { label: context.t("arrives"),  value: getTranslation(weather.schedule?.arrives) },
        allOnBoard: { label: context.t("all_on_board"), value: getTranslation(weather.schedule?.allOnBoard) },
        departs: { label: context.t("departs"),  value: getTranslation(weather.schedule?.departs) }
    }

    const portInformation = {
        currency: { label: context.t("currency"), value: getTranslation(weather.schedule?.currency) },
        language: { label: context.t("language"), value: getTranslation(weather.schedule?.language) },
        nameOfPier: { label: context.t("name_of_pier"), value: getTranslation(weather.schedule?.nameOfPier) },
        localAgentName: { label: context.t("ships_local_agent"), value: getTranslation(weather.schedule?.localAgentName) },
        gangway: { label: context.t("gangway"), value: getTranslation(weather.schedule?.gangway) },
    }

    return (
        <div className="grid-x grid-padding-x">
            <div className="cell small-12 medium-4 large-4">
                <DetailImage image={hubUrl + "blobs/image?reference=" + weather.schedule.id} />
            </div>
            <div className="cell small-12 medium-8 large-8 text-left">
                <h1 className='weather-heading'>
                    {weather.schedule.title.defaultValue}
                </h1>
                { isWeatherDataAvailable ?
                    <WeatherBoxContainer className="weather-container">
                        <div className="weather-icon-container">
                            <img
                                className="weather-icon"
                                src={`/images/weather/${weather.icon.substring(0,2)}.svg`}
                                alt={weatherObject.synopsis ? weatherObject.synopsis : "Weather icon"}
                            />
                        </div>
                        <WeatherInfoContainer>
                            { Object.entries(weatherObject).map(([key, { label, value, unit}]) =>
                                value && (
                                    <div key={key}>
                                        <b>{label}:</b> {value} {unit && unit}<br/>
                                    </div>
                                )
                            ) }
                        </WeatherInfoContainer>
                    </WeatherBoxContainer>
                    :
                    <strong>{context.t('weather_unavailable')}</strong>
                }
                { !!weather.schedule &&
                    <div>
                        { Object.values(allAboard).some(prop => prop?.value) &&
                            <div className='weather-schedule-heading'>
                                {context.t("all_aboard_times")}
                            </div>
                        }
                        { Object.entries(allAboard).map(([key, { label, value }]) =>
                            value && (
                                <div key={key}
                                     className="weather-schedule-field"
                                >
                                    <b>{label}:</b> {value}
                                </div>
                            )
                        ) }
                        { Object.values(portInformation).some(prop => prop?.value) &&
                            <div className='weather-schedule-heading'>
                                {context.t("port_information")}
                            </div>
                        }
                        { Object.entries(portInformation).map(([key, { label, value }]) =>
                            value && (
                                <div key={key}
                                     className="weather-schedule-field"
                                >
                                    <b>{label}:</b> {value}
                                </div>
                            )
                        ) }
                    </div>
                }
            </div>
        </div>
    );
};

WeatherInformation.contextTypes = {
    t: PropTypes.func.isRequired
};

export default WeatherInformation;
